import { type RefObject, useEffect, useId, useMemo, useRef } from 'react';
import type { FieldErrors, FieldValues } from 'react-hook-form';

import type { FormServerErrors } from './Form';
import { type ErrorSummaryItem, readErrorSummaryItems, readServerErrorSummaryItems } from './form-error-summary-utils';

interface UseFormErrorSummaryArgs<TFieldValues extends FieldValues> {
  errors: FieldErrors<TFieldValues>;
  serverErrors: FormServerErrors | undefined;
  submitCount: number;
}

interface UseFormErrorSummaryResult {
  errorSummaryItems: ErrorSummaryItem[];
  errorSummaryRef: RefObject<HTMLDivElement | null>;
  errorSummaryTitleId: string;
}

export function useFormErrorSummary<TFieldValues extends FieldValues>({
  errors,
  serverErrors,
  submitCount,
}: UseFormErrorSummaryArgs<TFieldValues>): UseFormErrorSummaryResult {
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const errorSummaryTitleId = useId();
  const errorSummaryItems = useMemo(() => {
    const uniqueItems = new Map<string, ErrorSummaryItem>();
    for (const item of [...readErrorSummaryItems(errors), ...readServerErrorSummaryItems(serverErrors)]) {
      uniqueItems.set(`${item.field}:${item.message}`, item);
    }
    return [...uniqueItems.values()];
  }, [errors, serverErrors]);

  useEffect(() => {
    if (submitCount > 0 && errorSummaryItems.length > 0) errorSummaryRef.current?.focus();
  }, [errorSummaryItems.length, submitCount]);

  return { errorSummaryItems, errorSummaryRef, errorSummaryTitleId };
}
